import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Search, ChevronDown, Trash2, Edit2, Eye, X, Check, User, Users, ShieldCheck, Coins } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';

export default function AdminUsers() {
  const { token, API } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editPoints, setEditPoints] = useState('');
  const [editName, setEditName] = useState('');
  const [viewDetail, setViewDetail] = useState(null);
  const limit = 20;

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search, sort_by: sortBy, sort_dir: sortDir, skip: page * limit, limit }
      });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
  }, [API, token, search, sortBy, sortDir, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const deleteUser = async (userId) => {
    if (!window.confirm('Delete this user and all their data?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchUsers();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const updateUser = async (userId) => {
    const updates = {};
    if (editName) updates.name = editName;
    if (editPoints !== '') updates.points = parseInt(editPoints);
    try {
      await axios.put(`${API}/admin/users/${userId}`, updates, { headers: { Authorization: `Bearer ${token}` } });
      setEditingUser(null);
      setEditName('');
      setEditPoints('');
      fetchUsers();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const viewUserDetail = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      setViewDetail(res.data);
    } catch (e) { console.error(e); }
  };

  const sortOptions = [
    { label: 'Newest', value: 'created_at', dir: 'desc' },
    { label: 'Oldest', value: 'created_at', dir: 'asc' },
    { label: 'Most Points', value: 'points', dir: 'desc' },
    { label: 'Highest Level', value: 'level', dir: 'desc' },
    { label: 'Most Links', value: 'links_protected', dir: 'desc' },
  ];

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            data-testid="admin-users-search"
            placeholder="Search by name or email..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            className="bg-[#121214] border-white/10 text-white pl-10 h-10 focus:border-amber-500/50"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">Sort:</span>
          <select
            data-testid="admin-users-sort"
            value={`${sortBy}-${sortDir}`}
            onChange={e => {
              const opt = sortOptions.find(o => `${o.value}-${o.dir}` === e.target.value);
              if (opt) { setSortBy(opt.value); setSortDir(opt.dir); }
            }}
            className="bg-[#121214] border border-white/10 text-white text-xs rounded-lg px-3 py-2 focus:border-amber-500/50 outline-none"
          >
            {sortOptions.map(o => (
              <option key={`${o.value}-${o.dir}`} value={`${o.value}-${o.dir}`}>{o.label}</option>
            ))}
          </select>
          <span className="text-xs text-zinc-500 ml-2">{total} users</span>
        </div>
      </div>

      {/* Users Table */}
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">User</th>
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">Points</th>
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">Level</th>
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">Links</th>
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">Creators</th>
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">Joined</th>
                <th className="text-right text-xs font-medium text-zinc-500 px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <motion.tr
                  key={u.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.02 }}
                  className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors"
                  data-testid={`admin-user-row-${i}`}
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="hex-clip w-8 h-8 bg-gradient-to-br from-amber-500/30 to-amber-700/30 flex items-center justify-center flex-shrink-0">
                        <User className="w-3.5 h-3.5 text-amber-500" />
                      </div>
                      <div>
                        <p className="text-sm text-white font-medium">{u.name}</p>
                        <p className="text-[10px] text-zinc-500">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-sm font-mono text-amber-500">{u.points || 0}</span>
                  </td>
                  <td className="px-5 py-3">
                    <Badge variant="outline" className="border-white/10 text-zinc-300 text-xs">
                      Lvl {u.level || 1}
                    </Badge>
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-sm text-zinc-300">{u.links_protected || 0}</span>
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-sm text-zinc-300">{u.creators_supported || 0}</span>
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-xs text-zinc-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        data-testid={`admin-view-user-${i}`}
                        onClick={() => viewUserDetail(u.id)}
                        className="p-1.5 rounded-md text-zinc-500 hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
                        title="View details"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`admin-edit-user-${i}`}
                        onClick={() => { setEditingUser(u); setEditName(u.name); setEditPoints(String(u.points || 0)); }}
                        className="p-1.5 rounded-md text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
                        title="Edit user"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`admin-delete-user-${i}`}
                        onClick={() => deleteUser(u.id)}
                        className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        title="Delete user"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {users.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">No users found</p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-white/5 px-5 py-3 flex items-center justify-between">
            <p className="text-xs text-zinc-500">Page {page + 1} of {totalPages}</p>
            <div className="flex gap-2">
              <button
                data-testid="admin-users-prev"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="px-3 py-1 rounded-md text-xs bg-white/5 text-zinc-400 hover:bg-white/10 disabled:opacity-30"
              >
                Prev
              </button>
              <button
                data-testid="admin-users-next"
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1 rounded-md text-xs bg-white/5 text-zinc-400 hover:bg-white/10 disabled:opacity-30"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Edit User</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 py-2">
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Name</label>
                <Input
                  data-testid="admin-edit-name"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  className="bg-[#09090B] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Points</label>
                <Input
                  data-testid="admin-edit-points"
                  type="number"
                  value={editPoints}
                  onChange={e => setEditPoints(e.target.value)}
                  className="bg-[#09090B] border-white/10 text-white"
                />
                <p className="text-[10px] text-zinc-600 mt-1">Level auto-calculated: Lvl {Math.floor(parseInt(editPoints || 0) / 50) + 1}</p>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  data-testid="admin-edit-cancel"
                  onClick={() => setEditingUser(null)}
                  className="flex-1 px-4 py-2 rounded-lg text-sm bg-white/5 text-zinc-400 hover:bg-white/10"
                >
                  Cancel
                </button>
                <button
                  data-testid="admin-edit-save"
                  onClick={() => updateUser(editingUser.id)}
                  className="flex-1 honey-btn px-4 py-2 rounded-lg text-sm"
                >
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View User Detail Dialog */}
      <Dialog open={!!viewDetail} onOpenChange={() => setViewDetail(null)}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">User Details</DialogTitle>
          </DialogHeader>
          {viewDetail && (
            <div className="space-y-5 py-2">
              <div className="flex items-center gap-4">
                <div className="hex-clip w-14 h-14 bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
                  <User className="w-6 h-6 text-black" />
                </div>
                <div>
                  <p className="text-base font-bold text-white">{viewDetail.user.name}</p>
                  <p className="text-xs text-zinc-500">{viewDetail.user.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="glass-card rounded-lg p-3 text-center">
                  <Coins className="w-4 h-4 text-amber-500 mx-auto mb-1" />
                  <p className="font-mono font-bold text-white">{viewDetail.user.points || 0}</p>
                  <p className="text-[10px] text-zinc-500">Points</p>
                </div>
                <div className="glass-card rounded-lg p-3 text-center">
                  <ShieldCheck className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                  <p className="font-mono font-bold text-white">{viewDetail.user.links_protected || 0}</p>
                  <p className="text-[10px] text-zinc-500">Links</p>
                </div>
                <div className="glass-card rounded-lg p-3 text-center">
                  <User className="w-4 h-4 text-blue-400 mx-auto mb-1" />
                  <p className="font-mono font-bold text-white">Lvl {viewDetail.user.level || 1}</p>
                  <p className="text-[10px] text-zinc-500">Level</p>
                </div>
              </div>

              {viewDetail.recent_links.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-zinc-400 mb-2">Recent Links</p>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {viewDetail.recent_links.map((l, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs px-2 py-1.5 rounded bg-white/[0.02]">
                        <span className="text-zinc-300 flex-1 truncate">{l.creator_name}</span>
                        <span className="text-zinc-600">{l.domain}</span>
                        <span className="font-mono text-amber-500">+{l.points_earned}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {viewDetail.recent_activity.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-zinc-400 mb-2">Recent Activity</p>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {viewDetail.recent_activity.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs px-2 py-1.5 rounded bg-white/[0.02]">
                        <span className="text-zinc-300 flex-1 truncate">{a.description}</span>
                        <span className="font-mono text-amber-500">+{a.points}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-[10px] text-zinc-600">
                Joined: {new Date(viewDetail.user.created_at).toLocaleString()}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
